import axios from 'axios';
import { CHECK_REGISTRATION_STATUS, GET_REGISTRATION_RACERS } from 'modules/registration/constants';
import { generatePath } from 'react-router-dom';
import shortid from 'shortid';

import {
  API_URL,
  API_REGISTRATION,
  API_REGISTRATION_STATUS,
  API_REGISTRATION_RACERS,
  CHANGE_DISTANCE_CONFIRM_END_POINT,
} from 'src/constants';

import { request, action } from 'utils';

import { Request, Response } from '../types';

class RegistrationService {
  @request({ action: shortid() })
  _submitRequest(raceId: number, registrationToken: string | nil, data: Request.Registration.SingleGroup | Request.Registration.Team): any {
    const url = generatePath(API_URL + API_REGISTRATION, { raceId });

    const headers = {};

    if (registrationToken) {
      headers['registration-token'] = registrationToken;
    }

    const request = axios({
      method: 'POST',
      url,
      headers,
      data,
    });

    // Add response logging
    // request.then(
    //   (response) => {
    //     console.log('✅ REGISTRATION API CALL - Success Response:', {
    //       status: response.status,
    //       statusText: response.statusText,
    //       data: response.data,
    //       payment_method_sent: data.payment_method,
    //       timestamp: new Date().toISOString(),
    //     });
    //   },
    //   (error) => {
    //     console.error('❌ REGISTRATION API CALL - Error Response:', {
    //       status: error.response?.status,
    //       statusText: error.response?.statusText,
    //       data: error.response?.data,
    //       payment_method_sent: data.payment_method,
    //       timestamp: new Date().toISOString(),
    //       error: error.message,
    //     });
    //   },
    // );

    return request;
  }

  @request({ action: CHECK_REGISTRATION_STATUS })
  _checkRegistrationStatusRequest(uuid: string): any {
    const url = generatePath(API_URL + API_REGISTRATION_STATUS, { uuid });

    return axios({ method: 'GET', url });
  }

  @request({ action: GET_REGISTRATION_RACERS })
  _checkRegistrationRacersRequest(registrationId: number): any {
    const url = generatePath(API_URL + API_REGISTRATION_RACERS, { registrationId });

    return axios({ method: 'GET', url });
  }

  @request({ action: shortid() })
  _submitChangeDistanceRequest(raceId: number, data: Request.Registration.ChangeDistance): any {
    const url = generatePath(API_URL + CHANGE_DISTANCE_CONFIRM_END_POINT, { raceId });

    return axios({ method: 'POST', url, data });
  }

  @action({ action: shortid() })
  async submitChangeDistance(
    raceId: number,
    data: Request.Registration.ChangeDistance,
  ): Promise<{
    data: null | Response.Registration.SingleGroup;
    isOk: boolean;
    status?: number;
    errors: RawError;
    message: string | null;
  }> {
    const [isOk, response] = await this._submitChangeDistanceRequest(raceId, data);

    return {
      isOk,
      status: response?.status,
      data: response.data || null,
      errors: response.data.errors || null,
      message: response.statusText,
    };
  }

  @action({ action: shortid() })
  /**
   * @description
   * submit group registrations
   */
  async submitSingleGroup(
    raceId: number,
    registrationToken: string | nil,
    data: Request.Registration.SingleGroup,
  ): Promise<{
    data: null | Response.Registration.SingleGroup;
    isOk: boolean;
    status?: number;
    errors: RawError;
    message: string | null;
  }> {
    const [isOk, response] = await this._submitRequest(raceId, registrationToken, data);
    return {
      isOk,
      status: response?.status,
      data: response.data || null,
      errors: response.data.errors || null,
      message: response.statusText,
    };
  }

  @action({ action: shortid() })
  /**
   * @description
   * submit group registrations
   */
  async submitTeam(
    raceId: number,
    registrationToken: string | nil,
    data: Request.Registration.Team,
  ): Promise<{
    data: null | Response.Registration.SingleGroup;
    isOk: boolean;
    status?: number;
    errors: RawError;
    message: string | null;
  }> {
    const [isOk, response] = await this._submitRequest(raceId, registrationToken, data);

    return {
      isOk,
      status: response?.status,
      data: response.data || null,
      errors: response.data.errors || null,
      message: response.data.message,
    };
  }

  @action({ action: CHECK_REGISTRATION_STATUS })
  /**
   * @description
   * check the registration status by registration uuid
   */
  async checkRegistrationStatus(uuid: string): Promise<{
    data: { data: null | Response.Registration.Status };
    isOk: boolean;
    errors: RawError;
    message: string | null;
  }> {
    const [isOk, response] = await this._checkRegistrationStatusRequest(uuid);

    return {
      isOk,
      data: response.data || null,
      errors: response.data.errors || null,
      message: response.data.message,
    };
  }

  @action({ action: GET_REGISTRATION_RACERS })
  /**
   * @description
   * get the registration racers by registration id
   */
  async getRegistrationRacers(registrationId: number): Promise<{
    data: { data: null | Response.Registration.Status };
    isOk: boolean;
    errors: RawError;
    message: string | null;
  }> {
    const [isOk, response] = await this._checkRegistrationRacersRequest(registrationId);

    return {
      isOk,
      data: response.data || null,
      errors: response.data.errors || null,
      message: response.data.message,
    };
  }
}

export { RegistrationService };
